import { memo, useCallback, useMemo } from 'react';
import {
  OwcTable,
  OwcTableHeader,
  OwcTableHeaderCell,
  OwcTableBody,
  OwcTableRow,
  OwcTableCell,
  OwcInput,
  OwcIcon,
} from '@one/react';
import Tabs from '../../../../components/Common/Tabs';
import EntityTypeChip from '../../../../components/Common/EntityTypeChip';
import EntityPath from '../../../../components/Common/EntityPath';
import styles from './DeprecateReleaseModal.module.css';
import tbl from '../../../../components/Common/entityTable.module.css';

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'event', label: 'Events' },
  { key: 'resource', label: 'Resources' },
  { key: 'cdt', label: 'CDTs' },
];

// Selectable row keyed by PK (not OwcTable's index-based API), so selection survives tab/filter changes. Memoized.
const EntityRow = memo(function EntityRow({
  entity,
  selected,
  releaseVersion,
  showType,
  onToggle,
}) {
  // Selected rows are inactivated; the rest follow the dry-run action (untag).
  const effectiveAction = selected ? 'inactivate' : entity.action;
  return (
    <OwcTableRow
      selectable
      selected={selected}
      onSelectedChange={(e) => onToggle(entity.pk, e.detail)}
    >
      <OwcTableCell align="left" valign="middle">
        <div className={tbl.entityName}>
          <strong>{entity.name}</strong>
          {entity.orphan && <span className={tbl.orphanBadge}>Orphan risk</span>}
        </div>
      </OwcTableCell>
      <OwcTableCell align="left" valign="middle">
        <EntityPath parent={entity.parent} />
      </OwcTableCell>
      {showType && (
        <OwcTableCell align="left" valign="middle">
          <EntityTypeChip type={entity.type} />
        </OwcTableCell>
      )}
      <OwcTableCell align="left" valign="middle">
        <span
          className={`${tbl.badge} ${
            effectiveAction === 'inactivate' ? tbl.badgeInactivate : tbl.badgeUntag
          }`}
        >
          {effectiveAction}
        </span>
        <span className={tbl.versionOld}>{releaseVersion}</span>
        {entity.newVer && <span className={tbl.versionNew}> → {entity.newVer}</span>}
      </OwcTableCell>
    </OwcTableRow>
  );
});

// Entity table (tabs + filter) with per-row PK selection; renders all filtered rows and scrolls via .tableWrap.
function DeprecationPlanTable({
  visibleEntities,
  selectedPks,
  releaseVersion,
  activeTab,
  search,
  showOrphansOnly,
  orphanCount = 0,
  onTabChange,
  onSearchChange,
  onOrphansToggle,
  onToggleVisible,
}) {
  const selectedSet = useMemo(() => new Set(selectedPks), [selectedPks]);

  // The Type column is only meaningful in the "All" tab; the type-specific tabs
  // already scope rows to a single type, so the column is redundant there.
  const showType = activeTab === 'all';

  // Stable identity so memoized rows aren't all re-rendered on each toggle; select-all flows through here too.
  const handleToggle = useCallback(
    (pk, checked) => onToggleVisible([pk], checked),
    [onToggleVisible]
  );

  return (
    <>
      <div className={styles.filters}>
        <Tabs tabs={TABS} active={activeTab} onChange={onTabChange} ariaLabel="Filter by type" />
        <div className={styles.filtersRight}>
          <OwcInput
            variant="outlined"
            size="small"
            rounded
            placeholder="Filter by name or parent..."
            value={search}
            aria-label="Filter entities by name or parent"
            onInput={(e) => onSearchChange(e.target.value)}
            onClearClick={() => onSearchChange('')}
            style={{ width: 220 }}
          >
            <OwcIcon slot="prefix" name="search" family="outlined" />
          </OwcInput>
        </div>
      </div>

      {visibleEntities.length === 0 ? (
        // Outside OwcTable: it doesn't reliably honor a colSpan'd full-width cell.
        <div className={styles.emptyState}>No entities match the current filter.</div>
      ) : (
        <div className={styles.tableWrap}>
          <OwcTable verticalBorder stickyHeader size="small" showSelectionColumn>
            <OwcTableHeader>
              <OwcTableHeaderCell width="auto" align="left">
                Name
              </OwcTableHeaderCell>
              <OwcTableHeaderCell width="auto" align="left">
                Parent
              </OwcTableHeaderCell>
              {showType && (
                <OwcTableHeaderCell width="110px" align="left">
                  Type
                </OwcTableHeaderCell>
              )}
              <OwcTableHeaderCell width="200px" align="left">
                Action
              </OwcTableHeaderCell>
            </OwcTableHeader>
            <OwcTableBody>
              {visibleEntities.map((entity) => (
                <EntityRow
                  key={entity.pk}
                  entity={entity}
                  selected={selectedSet.has(entity.pk)}
                  releaseVersion={releaseVersion}
                  showType={showType}
                  onToggle={handleToggle}
                />
              ))}
            </OwcTableBody>
          </OwcTable>
        </div>
      )}
    </>
  );
}

// Memoized: avoids re-rendering the whole OWC table on unrelated parent state changes.
export default memo(DeprecationPlanTable);
